import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { bt, btp } from '../i18n';
import { useBuilderStore } from '../state/builderStore';
import { useGenerationStore } from '../state/generation/generationStore';
import { sectionDisplayName } from '../shell/sectionVariantLabel';
import { blockLabel, fieldLabel } from './CanvasFrame';
import { aiKeysApi, type AiProviderChoices } from '../lib/aiKeysApi';
import { deriveDashboardUrl } from '../boot/config';
import { scopeLabel, type RetouchScope } from '../state/generation/retouche';

/**
 * LE NOM QU'UNE VENDEUSE CONNAÎT — jamais l'identifiant technique de la clé.
 *
 * « google » ne dit rien à personne : ce qu'elle a acheté s'appelle Nano Banana. La table reste
 * courte volontairement — un fournisseur absent d'ici s'affiche sous son identifiant plutôt que de
 * disparaître du menu.
 */
const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Claude',
  higgsfield: 'Higgsfield',
  google: 'Nano Banana',
};
const providerLabel = (id: string) => PROVIDER_LABELS[id] ?? id;

/**
 * CE QU'IL S'EST PASSÉ, EN UNE PHRASE — jamais un silence (27/08).
 *
 * Trois issues, trois phrases : ça a marché et on dit quoi ; le fournisseur a refusé ; ou il a
 * répondu sans rien changer — ce qui arrive quand la consigne ne concernait aucun texte de la
 * sélection, et qui se lisait jusqu'ici comme une panne.
 */
function outcomeLine(
  error: string | null,
  result: { texts: number; images: number; failedBatches: number } | null,
): string {
  if (error === 'indisponible') return bt('chrome.ask.no_ai', 'No AI is configured for that.');
  if (error) return bt('chrome.ask.refused', 'The provider refused. Try again.');
  const parts: string[] = [];
  if (result?.texts)
    parts.push(
      result.texts > 1
        ? btp('chrome.ask.texts_n', '{count} texts rewritten', { count: result.texts })
        : bt('chrome.ask.texts_1', '1 text rewritten'),
    );
  if (result?.images)
    parts.push(
      result.images > 1
        ? btp('chrome.ask.images_n', '{count} images redone', { count: result.images })
        : bt('chrome.ask.images_1', '1 image redone'),
    );
  if (parts.length === 0)
    return bt(
      'chrome.ask.nothing',
      'Nothing changed: the instruction didn’t target any text here.',
    );
  const suffix =
    result && result.failedBatches > 0 ? bt('chrome.ask.partial', ' (part of it failed)') : '';
  return `${parts.join(' · ')}${suffix}${bt('chrome.ask.undo_hint', '. Ctrl-Z undoes.')}`;
}

/**
 * UNE SURFACE À RETOUCHER — « les textes » ou « les images » — et QUI s'en charge.
 *
 * Les deux pastilles s'appelaient « Textes » et « Images », et le fondateur a buté dessus le 27/08 :
 * « je ne comprends pas ce que texte et image veulent dire ici ». Un nom de matière ne dit pas ce
 * qui va se passer. Elles disent maintenant l'ACTION (« Réécrire les textes »), avec en dessous ce
 * que ça touche vraiment, et à droite l'IA qui va le faire.
 */
function SurfaceRow({
  on,
  onToggle,
  label,
  hint,
  providers,
  missing,
  provider,
  onPick,
}: {
  on: boolean;
  onToggle: () => void;
  label: string;
  hint: string;
  /** Ceux dont la clé est posée : sélectionnables. */
  providers: string[];
  /** Ceux qui savent le faire mais dont la clé manque : montrés, éteints. */
  missing: string[];
  provider: string;
  onPick: (provider: string) => void;
}) {
  return (
    <div className={`bld-askpop__surface${on ? ' is-on' : ''}`}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        className="bld-askpop__surfacebtn"
        onClick={onToggle}
      >
        <span className="bld-askpop__tick" aria-hidden="true">
          {on ? '✓' : ''}
        </span>
        <span>
          {label}
          <span className="bld-askpop__hint">{hint}</span>
        </span>
      </button>
      {/* LE MENU EST TOUJOURS LÀ, ET RIEN N'Y EST ÉTEINT (27/08, seconde correction).
          Les fournisseurs sans clé s'affichaient grisés, et grisé se lit « il ne sait pas faire » :
          le fondateur a demandé « pourquoi Higgsfield et Nano Banana ne savent pas dessiner ? ».
          Ils savent. Il leur manque une clé — ce n'est pas une incapacité, c'est un branchement.
          Ils se choisissent donc normalement, et c'est une PHRASE qui dit quoi faire. */}
      {on && (providers.length > 0 || missing.length > 0) && (
        <label className="bld-askpop__with">
          <span className="bld-askpop__withword">{bt('chrome.ask.with', 'with')}</span>
          <select
            className="bld-askpop__select"
            aria-label={btp('chrome.ask.ai_for', 'AI for: {name}', { name: label })}
            value={provider}
            onChange={(e) => onPick(e.target.value)}
          >
            {[...providers, ...missing].map((p) => (
              <option key={p} value={p}>
                {providerLabel(p)}
                {missing.includes(p) ? bt('chrome.ask.to_connect', ' (needs a key)') : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      {on && missing.includes(provider) && (
        <p className="bld-askpop__needkey">
          {btp('chrome.ask.key_missing', '{name} can do it, but your key is missing.', {
            name: providerLabel(provider),
          })}{' '}
          <a href={`${deriveDashboardUrl()}/settings/ai`} target="_blank" rel="noreferrer">
            {bt('chrome.ask.connect', 'Connect it →')}
          </a>
        </p>
      )}
    </div>
  );
}

/**
 * «Demander un changement» (feature 0059 lot 2, élargi les 26 et 27/08) — la retouche brique par
 * brique que le fondateur a demandée : une consigne, et CE QUI EST SÉLECTIONNÉ se régénère sur place.
 *
 *  - LA PORTÉE SUIT LA SÉLECTION, et la pastille l'annonce en toutes lettres AVANT qu'elle écrive.
 *  - LE MODÈLE SAIT OÙ IL ÉCRIT : produit de la page, ce que vend la boutique, enchaînement des
 *    sections (voir `retouche.ts`).
 *  - ELLE CHOISIT SON IA ICI, et nulle part ailleurs (le routage a quitté les Réglages le 27/08).
 *    Le menu montre ce qui va servir MAINTENANT ; le changer vaut aussi pour la fois d'après, sinon
 *    il faudrait le refaire à chaque prompt.
 *
 * Entrée envoie, Échap ferme ; tout atterrit comme une édition normale, donc Ctrl-Z reste le bouton
 * « j'aime pas ».
 */
export function AskChangesPopover({
  sectionId,
  onClose,
}: {
  sectionId: string;
  onClose: () => void;
}) {
  const [value, setValue] = useState('');
  const [texts, setTexts] = useState(true);
  const [image, setImage] = useState(true);
  const regenError = useGenerationStore((s) => s.regenError);
  // LA BULLE RESTE OUVERTE PENDANT LE TRAVAIL (27/08). Elle se refermait sur l'envoi : la seule
  // chose que la vendeuse voyait ensuite était un « Régénère… » muet — deux minutes, puis rien.
  // Elle voit maintenant l'avancement, puis ce qui a changé, ou pourquoi ça n'a pas marché.
  const busy = useGenerationStore((s) => s.regenSectionId === sectionId);
  const progress = useGenerationStore((s) => s.regenProgress);
  const result = useGenerationStore((s) => s.regenResult);
  const [sent, setSent] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => inputRef.current?.focus(), []);

  // LA PORTÉE, telle que le canvas la connaît : un champ visé l'emporte sur un bloc, un bloc sur la
  // section. C'est exactement l'ordre du cadre de sélection — la pastille ne doit jamais annoncer une
  // portée différente de celle que la vendeuse voit encadrée.
  const selectedBlockId = useBuilderStore((s) => s.selectedBlockId);
  const focusedFieldId = useBuilderStore((s) => s.focusedFieldId);
  const section = useBuilderStore((s) => s.sectionsById[sectionId]);
  const scope: RetouchScope = useMemo(
    () => ({
      sectionId,
      blockId: focusedFieldId ? undefined : (selectedBlockId ?? undefined),
      fieldId: focusedFieldId ?? undefined,
    }),
    [sectionId, selectedBlockId, focusedFieldId],
  );
  const wording = useMemo(
    () =>
      scopeLabel(scope, {
        section: section
          ? sectionDisplayName(section)
          : bt('chrome.ask.the_section', 'the section'),
        target: scope.fieldId
          ? fieldLabel(section, scope.fieldId)
          : scope.blockId
            ? blockLabel(section, scope.blockId)
            : undefined,
      }),
    [scope, section],
  );

  // LES IA QU'ELLE A POSÉES. Lecture unique et mise en cache (aiKeysApi) ; une panne rend un menu vide
  // et la retouche part sur la chaîne du serveur — jamais un blocage.
  const [choices, setChoices] = useState<AiProviderChoices | null>(null);
  const [textProvider, setTextProvider] = useState('');
  const [imageProvider, setImageProvider] = useState('');
  useEffect(() => {
    let alive = true;
    void aiKeysApi.choices().then((c) => {
      if (!alive) return;
      setChoices(c);
      // Ce qui va servir MAINTENANT : son choix mémorisé, sinon la première clé capable. Jamais une
      // case vide — « qui va faire ça ? » ne doit pas être une question sans réponse à l'écran.
      setTextProvider(c.preferred.text ?? c.text[0] ?? '');
      setImageProvider(c.preferred.image ?? c.image[0] ?? '');
    });
    return () => {
      alive = false;
    };
  }, []);
  const textChoices = choices?.text ?? [];
  const imageChoices = choices?.image ?? [];
  // Ce qui SAIT faire le travail mais n'a pas de clé chez elle. Claude n'apparaît pas côté images :
  // il ne dessine pas, et un choix qui ne commande rien est un mensonge d'interface.
  const textMissing = (choices?.wired?.text ?? []).filter((p) => !textChoices.includes(p));
  const imageMissing = (choices?.wired?.image ?? []).filter((p) => !imageChoices.includes(p));

  // On ne MÉMORISE que ce qui peut servir : ranger « Nano Banana » comme choix permanent alors
  // qu'aucune clé ne l'honore ferait repartir la boutique entière sur un fournisseur muet.
  const pickText = (p: string) => {
    setTextProvider(p);
    if (!textMissing.includes(p)) void aiKeysApi.prefer('text', p);
  };
  const pickImage = (p: string) => {
    setImageProvider(p);
    if (!imageMissing.includes(p)) void aiKeysApi.prefer('image', p);
  };

  // Une IA choisie mais pas branchée BLOQUE l'envoi : partir quand même dépenserait un appel pour
  // un refus, et la vendeuse lirait « le fournisseur a refusé » là où il fallait lire « branche-la ».
  const blocked =
    (texts && textMissing.includes(textProvider)) ||
    (image && imageMissing.includes(imageProvider));

  const send = () => {
    if (!texts && !image) return;
    setSent(true);
    void useGenerationStore.getState().regenerateSection(
      sectionId,
      value,
      { texts, image },
      {
        scope,
        textProvider: texts ? textProvider || undefined : undefined,
        imageProvider: image ? imageProvider || undefined : undefined,
      },
    );
  };

  return (
    <div
      className="bld-askpop"
      role="dialog"
      aria-label={bt('chrome.ask.aria', 'Ask for a change')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <p className="bld-askpop__scope">
        {bt('chrome.ask.scope', 'I’m retouching')} <strong>{wording}</strong>
      </p>
      {sent && (
        <div className="bld-askpop__status" role="status">
          {busy ? (
            <>
              <span className="bld-askpop__spin" aria-hidden="true" />
              <span>
                {progress && progress.total > 1
                  ? btp('chrome.ask.writing_batch', 'Writing… batch {done} of {total}', {
                      done: progress.done + (progress.done < progress.total ? 1 : 0),
                      total: progress.total,
                    })
                  : bt('chrome.ask.writing', 'Writing…')}
              </span>
            </>
          ) : (
            <span className={regenError ? 'bld-askpop__err' : undefined}>
              {outcomeLine(regenError, result)}
            </span>
          )}
          {!busy && (
            <button type="button" className="bld-askpop__again" onClick={() => setSent(false)}>
              {regenError
                ? bt('chrome.common.retry', 'Retry')
                : bt('chrome.ask.another', 'Another change')}
            </button>
          )}
        </div>
      )}
      <textarea
        ref={inputRef}
        className="bld-askpop__input"
        rows={2}
        maxLength={450}
        placeholder={bt(
          'chrome.ask.ph',
          'Describe the change: “shorter”, “premium tone”, “brighter image”…',
        )}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
      />
      <SurfaceRow
        on={texts}
        onToggle={() => setTexts((v) => !v)}
        label={bt('chrome.ask.rewrite_texts', 'Rewrite the texts')}
        hint={bt('chrome.ask.rewrite_hint', 'titles, paragraphs, buttons')}
        providers={textChoices}
        missing={textMissing}
        provider={textProvider}
        onPick={pickText}
      />
      <SurfaceRow
        on={image}
        onToggle={() => setImage((v) => !v)}
        label={bt('chrome.ask.redo_images', 'Redo the images')}
        hint={bt('chrome.ask.redo_hint', 'the photos and visuals of this area')}
        providers={imageChoices}
        missing={imageMissing}
        provider={imageProvider}
        onPick={pickImage}
      />
      <div className="bld-askpop__row">
        <span className="bld-askpop__spacer" />
        <button type="button" className="bld-askpop__close" onClick={onClose}>
          {bt('chrome.common.close', 'Close')}
        </button>
        <button
          type="button"
          className="bld-askpop__send"
          onClick={send}
          disabled={(!texts && !image) || busy || blocked}
        >
          {busy ? bt('chrome.ask.busy', 'Working…') : bt('chrome.ask.send', 'Regenerate')}
        </button>
      </div>
    </div>
  );
}
